// src/Components/Admin/SettingsOther.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// Import master presets from RichTextEditor
import { FONT_SIZES, ALLOWED_FONTS } from '../../utils/RichTextEditor';

const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

export default function SettingsOther({
  iconScale,
  handleIconScaleChange,
  incrementIconScale,
  decrementIconScale,
  websiteUrl,
  handleTextChange,
  emailFrom,
  setEmailFrom,
  makePublicDays,
  handleMakePublicChange,
  incrementMakePublic,
  decrementMakePublic,
  rteFontType,
  setRteFontType,
  rteFontSize,
  setRteFontSize,
  imHeadingFontType,
  setImHeadingFontType,
  imHeadingFontSize,
  setImHeadingFontSize,
  allowRowBreaks,
  setAllowRowBreaks,
  useRoundedCorners,
  setUseRoundedCorners,
  roundedCornerRadius,
  setRoundedCornerRadius,
  makeUppercase,
  setMakeUppercase,
  broadcastDelay,
  setBroadcastDelay,
  isSecurityGroupLocked,
  setIsSecurityGroupLocked,
  formatDelayString,
  openPasswordDialog,
  deleteBouncesLock,
  setDeleteBouncesLock,
  importLimit,
  setImportLimit,
}) {
  return (
    <>
      <Typography sx={{ color: '#000000', fontWeight: 'normal', mb: 0.5 }}>
        Other Settings
      </Typography>
      <TableContainer sx={{ mt: 1 }}>
        <Table size='small' sx={{ '& td, & th': { border: 0, py: 0.25, px: 1 } }}>
          <TableBody>
            {/* 3. ICON SIZE SECTION */}
            <TableRow>
              <TableCell sx={{ width: '40%' }}>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Icon Size %
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={iconScale}
                    onChange={handleIconScaleChange}
                    type='number'
                    variant='standard'
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '0.8rem',
                        fontWeight: 'normal',
                        textAlign: 'center',
                        color: DARK_BLUE,
                      },
                    }}
                    inputProps={{
                      style: { textAlign: 'center', padding: 0 },
                      min: 10,
                      max: 500,
                    }}
                    sx={{
                      width: '60px',
                      backgroundColor: LIGHT_BLUE_BG,
                      px: 1,
                      borderRadius: 1,
                      border: `1px solid ${ORANGE_COLOR}`,
                      '& .MuiInputBase-input': {
                        paddingTop: '4px',
                        paddingBottom: '5px',
                      },
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                        {
                          display: 'none',
                        },
                      '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                      '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      height: '30px',
                    }}>
                    <IconButton
                      onClick={incrementIconScale}
                      sx={{
                        p: 0,
                        lineHeight: 0,
                        height: '15px',
                        width: '24px',
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      }}>
                      <ArrowDropUpIcon
                        fontSize='medium'
                        sx={{ color: '#000', transform: 'translateY(2px)' }}
                      />
                    </IconButton>

                    <IconButton
                      onClick={decrementIconScale}
                      sx={{
                        p: 0,
                        lineHeight: 0,
                        height: '15px',
                        width: '24px',
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      }}>
                      <ArrowDropDownIcon
                        fontSize='medium'
                        sx={{ color: '#000', transform: 'translateY(-2px)' }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>

            {/* 4. MAIN WEBSITE SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Main Web Site
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    placeholder='www.mainwebsite.com'
                    value={websiteUrl}
                    onChange={handleTextChange}
                    variant='standard'
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '0.8rem',
                        fontWeight: 'normal',
                        color: DARK_BLUE,
                      },
                    }}
                    sx={{
                      backgroundColor: LIGHT_BLUE_BG,
                      px: 1,
                      borderRadius: 1,
                      border: `1px solid ${ORANGE_COLOR}`,
                      '& .MuiInputBase-input': {
                        color: DARK_BLUE,
                        fontWeight: 'normal',
                        paddingTop: '4px',
                        paddingBottom: '5px',
                      },
                      '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                      '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>

            {/* 4b. EMAIL FROM SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Broadcast 'From' Email Address
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    placeholder='info@yourdomain.com'
                    value={emailFrom}
                    onChange={(e) => setEmailFrom(e.target.value)}
                    variant='standard'
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '0.8rem',
                        fontWeight: 'normal',
                        color: DARK_BLUE,
                      },
                    }}
                    sx={{
                      backgroundColor: LIGHT_BLUE_BG,
                      px: 1,
                      borderRadius: 1,
                      border: `1px solid ${ORANGE_COLOR}`,
                      '& .MuiInputBase-input': {
                        color: DARK_BLUE,
                        fontWeight: 'normal',
                        paddingTop: '4px',
                        paddingBottom: '5px',
                      },
                      '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                      '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>

            {/* 5. DEFAULT MAKE PUBLIC SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Default Make Public (days)
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={makePublicDays}
                    onChange={handleMakePublicChange}
                    type='text'
                    inputMode='numeric'
                    variant='standard'
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '0.8rem',
                        fontWeight: 'normal',
                        textAlign: 'center',
                        color: DARK_BLUE,
                      },
                    }}
                    inputProps={{
                      style: { textAlign: 'center', padding: 0 },
                    }}
                    sx={{
                      width: '60px',
                      backgroundColor: LIGHT_BLUE_BG,
                      px: 1,
                      borderRadius: 1,
                      border: `1px solid ${ORANGE_COLOR}`,
                      '& .MuiInputBase-input': {
                        paddingTop: '4px',
                        paddingBottom: '5px',
                      },
                      '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                      '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      height: '30px',
                    }}>
                    <IconButton
                      onClick={incrementMakePublic}
                      sx={{
                        p: 0,
                        lineHeight: 0,
                        height: '15px',
                        width: '24px',
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      }}>
                      <ArrowDropUpIcon
                        fontSize='medium'
                        sx={{ color: '#000', transform: 'translateY(2px)' }}
                      />
                    </IconButton>

                    <IconButton
                      onClick={decrementMakePublic}
                      sx={{
                        p: 0,
                        lineHeight: 0,
                        height: '15px',
                        width: '24px',
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      }}>
                      <ArrowDropDownIcon
                        fontSize='medium'
                        sx={{ color: '#000', transform: 'translateY(-2px)' }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>

            {/* 5b. RICH TEXT EDITOR DEFAULT TEXT SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Rich Text Editor Default Font & Size
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size='small' variant='standard' sx={{ minWidth: 120 }}>
                    <Select
                      value={rteFontType}
                      onChange={(e) => setRteFontType(e.target.value)}
                      disableUnderline
                      MenuProps={{ sx: { zIndex: 100009 } }}
                      sx={{
                        backgroundColor: LIGHT_BLUE_BG,
                        px: 1,
                        borderRadius: 1,
                        border: `1px solid ${ORANGE_COLOR}`,
                        fontSize: '0.8rem',
                        color: DARK_BLUE,
                        '& .MuiSelect-select': { py: 0.5 },
                      }}>
                      {ALLOWED_FONTS.map((f) => (
                        <MenuItem
                          key={f}
                          value={f}
                          sx={{ fontSize: '0.8rem', fontFamily: f }}>
                          {f}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size='small' variant='standard' sx={{ minWidth: 80 }}>
                    <Select
                      value={rteFontSize}
                      onChange={(e) => setRteFontSize(e.target.value)}
                      disableUnderline
                      MenuProps={{ sx: { zIndex: 100009 } }}
                      sx={{
                        backgroundColor: LIGHT_BLUE_BG,
                        px: 1,
                        borderRadius: 1,
                        border: `1px solid ${ORANGE_COLOR}`,
                        fontSize: '0.8rem',
                        color: DARK_BLUE,
                        '& .MuiSelect-select': { py: 0.5 },
                      }}>
                      {FONT_SIZES.map((s) => (
                        <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>
            </TableRow>

            {/* 5c. IMPORT MARKERS DEFAULT HEADING TEXT SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Import Markers Default Heading Font & Size
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size='small' variant='standard' sx={{ minWidth: 120 }}>
                    <Select
                      value={imHeadingFontType}
                      onChange={(e) => setImHeadingFontType(e.target.value)}
                      disableUnderline
                      MenuProps={{ sx: { zIndex: 100009 } }}
                      sx={{
                        backgroundColor: LIGHT_BLUE_BG,
                        px: 1,
                        borderRadius: 1,
                        border: `1px solid ${ORANGE_COLOR}`,
                        fontSize: '0.8rem',
                        color: DARK_BLUE,
                        '& .MuiSelect-select': { py: 0.5 },
                      }}>
                      {ALLOWED_FONTS.map((f) => (
                        <MenuItem
                          key={f}
                          value={f}
                          sx={{ fontSize: '0.8rem', fontFamily: f }}>
                          {f}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size='small' variant='standard' sx={{ minWidth: 80 }}>
                    <Select
                      value={imHeadingFontSize}
                      onChange={(e) => setImHeadingFontSize(e.target.value)}
                      disableUnderline
                      MenuProps={{ sx: { zIndex: 100009 } }}
                      sx={{
                        backgroundColor: LIGHT_BLUE_BG,
                        px: 1,
                        borderRadius: 1,
                        border: `1px solid ${ORANGE_COLOR}`,
                        fontSize: '0.8rem',
                        color: DARK_BLUE,
                        '& .MuiSelect-select': { py: 0.5 },
                      }}>
                      {FONT_SIZES.map((s) => (
                        <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>
            </TableRow>

            {/* 5d. ROUNDED CORNERS SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Use rounded corners in images
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Checkbox
                    size='small'
                    checked={useRoundedCorners}
                    onChange={(e) => setUseRoundedCorners(e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                  {useRoundedCorners && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', color: DARK_BLUE }}>
                        Radius (px):
                      </Typography>
                      <TextField
                        value={roundedCornerRadius}
                        onChange={(e) => setRoundedCornerRadius(e.target.value)}
                        type='number'
                        variant='standard'
                        size='small'
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            fontSize: '0.8rem',
                            textAlign: 'center',
                            color: DARK_BLUE,
                          },
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          style: { textAlign: 'center', padding: 0 },
                        }}
                        sx={{
                          width: '50px',
                          backgroundColor: LIGHT_BLUE_BG,
                          px: 1,
                          borderRadius: 1,
                          border: `1px solid ${ORANGE_COLOR}`,
                          '& .MuiInputBase-input': {
                            paddingTop: '4px',
                            paddingBottom: '5px',
                          },
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                            {
                              display: 'none',
                            },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </TableCell>
            </TableRow>

            {/* 6. PROMO DOCS ROW BREAK SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Allow rows in Promo Word/PDF docs to break across pages
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size='small'
                    checked={allowRowBreaks}
                    onChange={(e) => setAllowRowBreaks(e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                </Box>
              </TableCell>
            </TableRow>

            {/* 7. TEXT FORMATTING SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Make text in Info and Hover uppercase when typing
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size='small'
                    checked={makeUppercase}
                    onChange={(e) => setMakeUppercase(e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                </Box>
              </TableCell>
            </TableRow>

            {/* 8. BROADCAST DELAY SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Broadcast Delay HH:MM:SS
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  borderRight: `1px solid ${ORANGE_COLOR} !important`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={broadcastDelay}
                    onChange={(e) => setBroadcastDelay(e.target.value)}
                    disabled={isSecurityGroupLocked}
                    placeholder='48:00:00'
                    variant='standard'
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '0.8rem',
                        fontWeight: 'normal',
                        textAlign: 'center',
                        color: isSecurityGroupLocked ? 'gray' : DARK_BLUE,
                      },
                    }}
                    inputProps={{
                      style: { textAlign: 'center', padding: 0 },
                    }}
                    sx={{
                      width: '80px',
                      backgroundColor: LIGHT_BLUE_BG,
                      px: 1,
                      borderRadius: 1,
                      border: `1px solid ${ORANGE_COLOR}`,
                      '& .MuiInputBase-input': {
                        paddingTop: '4px',
                        paddingBottom: '5px',
                      },
                      '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                      '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
                      opacity: isSecurityGroupLocked ? 0.7 : 1,
                    }}
                  />
                </Box>
              </TableCell>
              {/* CONSOLIDATED SECURITY LOCK CELL */}
              <TableCell
                rowSpan={3}
                sx={{
                  verticalAlign: 'middle',
                  pl: 1.5,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                  }}>
                  <IconButton
                    onClick={() => {
                      if (!isSecurityGroupLocked) {
                        const formatted = formatDelayString(broadcastDelay);
                        setBroadcastDelay(formatted);
                        setIsSecurityGroupLocked(true);
                      }
                    }}
                    size='small'
                    sx={{ color: '#000000', mb: 1, p: 0.5 }}>
                    {isSecurityGroupLocked ? (
                      <LockIcon fontSize='small' />
                    ) : (
                      <LockOpenIcon fontSize='small' />
                    )}
                  </IconButton>

                  <IconButton
                    onClick={() => openPasswordDialog()}
                    size='small'
                    sx={{ color: DARK_BLUE, p: 0.5 }}
                    title='Security Password'>
                    <VpnKeyIcon fontSize='small' />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>

            {/* 9. UNSUBSCRIBED/BOUNCES DELETE LOCK SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Unsubscribed/Bounces Delete Lock
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  borderRight: `1px solid ${ORANGE_COLOR} !important`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: '80px',
                      display: 'flex',
                      justifyContent: 'center',
                      px: 1,
                    }}>
                    <Checkbox
                      size='small'
                      checked={deleteBouncesLock}
                      onChange={(e) => setDeleteBouncesLock(e.target.checked)}
                      disabled={isSecurityGroupLocked}
                      sx={{ p: 0.5, opacity: isSecurityGroupLocked ? 0.7 : 1 }}
                    />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>

            {/* 10. MAILING LIST IMPORT LIMIT SECTION */}
            <TableRow>
              <TableCell>
                <Typography
                  sx={{
                    color: '#000000',
                    fontWeight: 'normal',
                    fontSize: '0.85rem',
                  }}>
                  Mailing List Import Limit
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  borderRight: `1px solid ${ORANGE_COLOR} !important`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={importLimit}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d+$/.test(val)) {
                        setImportLimit(val === '' ? '' : Number(val));
                      }
                    }}
                    disabled={isSecurityGroupLocked}
                    placeholder='100'
                    variant='standard'
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '0.8rem',
                        fontWeight: 'normal',
                        textAlign: 'center',
                        color: isSecurityGroupLocked ? 'gray' : DARK_BLUE,
                      },
                    }}
                    inputProps={{
                      style: { textAlign: 'center', padding: 0 },
                    }}
                    sx={{
                      width: '80px',
                      backgroundColor: LIGHT_BLUE_BG,
                      px: 1,
                      borderRadius: 1,
                      border: `1px solid ${ORANGE_COLOR}`,
                      '& .MuiInputBase-input': {
                        paddingTop: '4px',
                        paddingBottom: '5px',
                      },
                      '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                      '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
                      opacity: isSecurityGroupLocked ? 0.7 : 1,
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
